/**
 * Move an interview through its status machine — the interview analogue of the
 * pipeline's `ApplicationStageControls`, and the same rule: **the illegal moves have
 * no DOM node.**
 *
 * `interviewTransitions(status)` is the whole list, mapped once. There is no loop
 * computing a `disabled` flag, because a disabled attribute is a rendering decision an
 * operator removes in devtools, and a greyed control asserts "the system will not let
 * you" about a move that exists for nobody. `completed`, `cancelled` and `no_show` are
 * terminal — their allowed set is `[]` — so a finished interview renders no move
 * buttons and one sentence.
 *
 * ── The refusal is the authority ─────────────────────────────────────────────
 *
 * A stale detail can still issue a move the server refuses `409
 * WORKFORCE_INVALID_STATE_TRANSITION`, and that refusal is rendered — the server's own
 * sentence through `ApiErrorNote`, and the server's own `allowed_transitions[]` printed
 * beside it rather than re-derived from this client's copy. Patching a status needs
 * `recruiting.manage_candidates`; a caller without it is refused `403` here and the
 * shared `PermissionDenied` state renders it.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { WorkforceApiError } from '../../api/errors';
import { allowedTransitionsFromRefusal } from '../../api/useApiMutation';
import {
  interviewTransitions,
  isTerminalInterviewStatus,
  INTERVIEW_STATUS_MOVE_LABEL,
  type InterviewStatus,
} from '../../recruiting/interview';
import { ApiErrorNote } from '../../states/ApiErrorNote';

export interface InterviewStatusControlsProps {
  readonly status: InterviewStatus;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly onMove: (to: InterviewStatus) => void;
}

export function InterviewStatusControls({
  status,
  pending,
  error,
  onMove,
}: InterviewStatusControlsProps): ReactNode {
  const { t } = useI18n();
  const moves = interviewTransitions(status);
  const serverAllowed = error === null ? [] : allowedTransitionsFromRefusal(error);

  return (
    <div className="wp-board__moves" data-testid="w4-status-controls">
      {isTerminalInterviewStatus(status) ? (
        <p className="wp-small wp-muted" data-testid="w4-status-terminal">
          {t('recruiting.interviews.status.terminal')}
        </p>
      ) : (
        <div className="wp-toggle">
          {moves.map((move) => (
            <button
              key={move}
              className="wp-button"
              type="button"
              data-testid={`w4-status-move-${move}`}
              disabled={pending}
              onClick={() => {
                onMove(move);
              }}
            >
              {t(INTERVIEW_STATUS_MOVE_LABEL[move])}
            </button>
          ))}
        </div>
      )}

      {error === null ? null : (
        <>
          <ApiErrorNote
            error={error}
            subject={t('recruiting.interviews.subjectWord')}
            testId="w4-status-error"
          />
          {serverAllowed.length === 0 ? null : (
            <p className="wp-small" data-testid="w4-status-allowed">
              {t('recruiting.interviews.status.serverAllowed', { list: serverAllowed.join(', ') })}
            </p>
          )}
        </>
      )}
    </div>
  );
}
